// stores/notificationCenter.ts
// Persistent in-app notification center: items that must stay visible until
// acted upon (unlike the 5s NotificationPlugin toasts), e.g. the file
// retention warning. Rendered in the Navigation bell drawer; producers
// (TranscriptorDashboard for retention) upsert/remove, the drawer reads.
import { defineStore } from 'pinia';

export interface NotificationRoute {
  name: string;
  query: Record<string, string>;
}

export interface NotificationAction {
  labelKey: string;
  route: NotificationRoute;
}

export interface CenterNotification {
  id: string;
  type: 'warning' | 'info' | 'danger' | 'success';
  /** i18n key rendered via $t(messageKey, messageParams) */
  messageKey: string;
  messageParams?: Record<string, unknown>;
  /** Extra context line (e.g. project name) — raw text, not translated. */
  meta?: string;
  createdAt: number;
  read: boolean;
  action?: NotificationAction;
}

export interface RetentionContext {
  count: number;
  workspaceId: string;
  projectId: string;
  projectName: string;
}

export const RETENTION_ID = 'retention';

export const useNotificationCenterStore = defineStore('notificationCenter', {
  state: () => ({
    items: [] as CenterNotification[],
    // Session-scoped dismissals so a producer watcher can't resurrect a
    // notification the user just dismissed. The key embeds the exact
    // context (e.g. expiring count), so a changed situation shows again.
    dismissedKeys: [] as string[],
    lastRetentionKey: '' as string,
  }),

  getters: {
    sortedItems: (state): CenterNotification[] =>
      [...state.items].sort((a, b) => b.createdAt - a.createdAt),
    unreadCount: (state): number => state.items.filter((i) => !i.read).length,
  },

  actions: {
    retentionKey(ctx: RetentionContext): string {
      return `retention:${ctx.workspaceId}:${ctx.projectId}:${ctx.count}`;
    },

    upsertRetention(ctx: RetentionContext) {
      if (!ctx.count || !ctx.projectId) return;
      const key = this.retentionKey(ctx);
      this.lastRetentionKey = key;
      if (this.dismissedKeys.includes(key)) return;
      const messageKey =
        ctx.count === 1
          ? '1 file will be auto-deleted in the next 7 days — pin it to keep it for 1 year.'
          : '{n} files will be auto-deleted in the next 7 days — pin them to keep them for 1 year.';
      const existing = this.items.find((i) => i.id === RETENTION_ID);
      const item: CenterNotification = {
        id: RETENTION_ID,
        type: 'warning',
        messageKey,
        messageParams: ctx.count === 1 ? undefined : { n: ctx.count },
        meta: ctx.projectName,
        createdAt: existing?.createdAt ?? Date.now(),
        read: existing?.read ?? false,
        action: {
          labelKey: 'Review',
          route: {
            name: 'TranscriptEditorDashboard',
            query: {
              workspace: ctx.workspaceId,
              project: ctx.projectId,
              expiring: '1',
            },
          },
        },
      };
      if (existing) Object.assign(existing, item);
      else this.items.push(item);
    },

    removeRetention() {
      this.items = this.items.filter((i) => i.id !== RETENTION_ID);
      this.dismissedKeys = this.dismissedKeys.filter((k) => !k.startsWith('retention:'));
      this.lastRetentionKey = '';
    },

    dismiss(id: string) {
      if (id === RETENTION_ID && this.lastRetentionKey) {
        if (!this.dismissedKeys.includes(this.lastRetentionKey)) {
          this.dismissedKeys.push(this.lastRetentionKey);
        }
      }
      this.items = this.items.filter((i) => i.id !== id);
    },

    clearAll() {
      // Clearing also dismisses the current retention context — otherwise
      // the producer watcher would re-add it on the next tick.
      if (this.lastRetentionKey && !this.dismissedKeys.includes(this.lastRetentionKey)) {
        this.dismissedKeys.push(this.lastRetentionKey);
      }
      this.items = [];
    },

    markAllRead() {
      this.items.forEach((i) => { i.read = true; });
    },
  },
});
